import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Shield, Settings2, Save } from 'lucide-react';
import { getErrorMessage } from '@/lib/errorUtils';

import { API } from '@/config/api';

// All available modules
const ALL_MODULES = [
  { id: 'dashboard', label: 'Dashboard', icon: '📊' },
  { id: 'calendar', label: 'Calendar', icon: '📅' },
  { id: 'rooms', label: 'Rooms', icon: '🛏️' },
  { id: 'reservations', label: 'Reservations', icon: '📋' },
  { id: 'guests', label: 'Guests', icon: '👥' },
  { id: 'billing', label: 'Billing', icon: '💰' },
  { id: 'expenses', label: 'Expenses', icon: '📉' },
  { id: 'housekeeping', label: 'Housekeeping', icon: '🧹' },
  { id: 'maintenance', label: 'Maintenance', icon: '🔧' },
  { id: 'transfers', label: 'Transfers', icon: '🚗' },
  { id: 'channels', label: 'Channels', icon: '🌐' },
  { id: 'reports', label: 'Reports', icon: '📈' },
  { id: 'staff', label: 'Staff', icon: '👔' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
];

// Actions available for each module
const ACTIONS = ['view', 'create', 'edit', 'delete'];

const StaffPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPermDialog, setShowPermDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'receptionist'
  });

  useEffect(() => {
    fetchStaff();
  }, [user?.hotel_id]);

  const fetchStaff = async () => {
    if (!user?.hotel_id) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/hotels/${user.hotel_id}/staff`, {
        headers: getAuthHeaders()
      });
      setStaff(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/hotels/${user.hotel_id}/staff`,
        formData,
        { headers: getAuthHeaders() }
      );
      
      setStaff(prev => [...prev, response.data]);
      setShowDialog(false);
      setFormData({ name: '', email: '', password: '', role: 'receptionist' });
      toast.success('Staff member added');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add staff'));
    }
  };

  const handleDelete = async (staffId) => {
    if (staffId === user?.id) {
      toast.error("You can't remove yourself");
      return;
    }
    if (!window.confirm('Are you sure you want to remove this staff member?')) return;
    
    try {
      await axios.delete(
        `${API}/hotels/${user.hotel_id}/staff/${staffId}`,
        { headers: getAuthHeaders() }
      );
      
      setStaff(prev => prev.filter(s => s.id !== staffId));
      toast.success('Staff member removed');
    } catch (error) {
      toast.error('Failed to remove staff member');
    }
  };

  const openPermissionsDialog = (member) => {
    setSelectedStaff(member);
    // Initialize permissions from member's current permissions or empty
    const perms = member.permissions || {};
    setEditingPermissions(perms);
    setShowPermDialog(true);
  };

  const toggleModuleAccess = (moduleId, hasAccess) => {
    setEditingPermissions(prev => {
      const updated = { ...prev };
      if (hasAccess) {
        // Grant view access by default
        updated[moduleId] = ['view'];
      } else {
        // Remove module access
        delete updated[moduleId];
      }
      return updated;
    });
  };

  const toggleAction = (moduleId, action) => {
    setEditingPermissions(prev => {
      const updated = { ...prev };
      const modulePerms = updated[moduleId] || [];
      
      if (modulePerms.includes(action)) {
        // Remove action
        updated[moduleId] = modulePerms.filter(a => a !== action);
        // If no actions left, remove module
        if (updated[moduleId].length === 0) {
          delete updated[moduleId];
        }
      } else {
        // Add action (ensure view is always included)
        if (!modulePerms.includes('view') && action !== 'view') {
          updated[moduleId] = [...modulePerms, 'view', action];
        } else {
          updated[moduleId] = [...modulePerms, action];
        }
      }
      return updated;
    });
  };

  const savePermissions = async () => {
    if (!selectedStaff) return;
    setSavingPerms(true);
    
    try {
      await axios.put(
        `${API}/hotels/${user.hotel_id}/staff/${selectedStaff.id}/permissions`,
        { permissions: editingPermissions },
        { headers: getAuthHeaders() }
      );
      
      // Update local state
      setStaff(prev => prev.map(s => 
        s.id === selectedStaff.id 
          ? { ...s, permissions: editingPermissions }
          : s
      ));
      
      toast.success(`Permissions updated for ${selectedStaff.name}`);
      setShowPermDialog(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update permissions'));
    } finally {
      setSavingPerms(false);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-amber-100 text-amber-700 border-amber-200',
      manager: 'bg-blue-100 text-blue-700 border-blue-200',
      receptionist: 'bg-green-100 text-green-700 border-green-200',
      housekeeping: 'bg-purple-100 text-purple-700 border-purple-200',
      accountant: 'bg-rose-100 text-rose-700 border-rose-200'
    };
    return styles[role] || 'bg-gray-100 text-gray-700';
  };

  const getAccessibleModules = (member) => {
    const perms = member.permissions || {};
    return Object.keys(perms).filter(m => perms[m]?.includes('view'));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Staff Management</h1>
          <p className="text-muted-foreground">Manage team members and their menu access</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-staff-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Staff Member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Staff Member</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Jean Dupont"
                  data-testid="staff-name-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="jean@hotel.com"
                  data-testid="staff-email-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="staff-password-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="staff-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="receptionist">Receptionist</SelectItem>
                    <SelectItem value="housekeeping">Housekeeping</SelectItem>
                    <SelectItem value="accountant">Accountant</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Role sets default permissions. You can customize after creation.
                </p>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-staff-btn">
                Add Staff Member
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Staff List with Permissions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Team Members ({staff.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {staff.map((member) => {
              const accessibleModules = getAccessibleModules(member);
              
              return (
                <div
                  key={member.id}
                  className="p-4 border rounded-lg hover:border-blue-200 dark:hover:border-blue-900 transition-colors"
                  data-testid={`staff-card-${member.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
                        <span className="text-lg font-medium text-blue-600">
                          {member.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-medium text-lg">{member.name}</h3>
                        <p className="text-sm text-muted-foreground">{member.email}</p>
                        <Badge className={`mt-1 ${getRoleBadge(member.role)}`}>
                          {member.role?.charAt(0).toUpperCase() + member.role?.slice(1)}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(user?.role === 'admin' || user?.role === 'super_admin') && member.id !== user?.id && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPermissionsDialog(member)}
                            data-testid={`edit-perms-btn-${member.id}`}
                          >
                            <Settings2 className="h-4 w-4 mr-1" />
                            Permissions
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(member.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-staff-btn-${member.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {member.id === user?.id && (
                        <Badge variant="outline">You</Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Module Access Summary */}
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Menu Access:</p>
                    <div className="flex flex-wrap gap-2">
                      {accessibleModules.length > 0 ? (
                        accessibleModules.map(moduleId => {
                          const module = ALL_MODULES.find(m => m.id === moduleId);
                          return module ? (
                            <Badge key={moduleId} variant="secondary" className="text-xs">
                              {module.icon} {module.label}
                            </Badge>
                          ) : null;
                        })
                      ) : (
                        <span className="text-sm text-muted-foreground italic">
                          Using default role permissions
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {staff.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No staff members yet. Add your first team member above.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Permissions Dialog */}
      <Dialog open={showPermDialog} onOpenChange={setShowPermDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Manage Permissions - {selectedStaff?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select which menu modules this staff member can access and what actions they can perform.
            </p>
            
            <div className="grid grid-cols-1 gap-3">
              {ALL_MODULES.map(module => {
                const hasAccess = editingPermissions[module.id]?.includes('view');
                const modulePerms = editingPermissions[module.id] || [];
                
                return (
                  <div 
                    key={module.id}
                    className={`p-4 border rounded-lg transition-colors ${hasAccess ? 'border-blue-200 bg-blue-50/50 dark:bg-blue-950/20' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id={`module-${module.id}`}
                          checked={hasAccess}
                          onCheckedChange={(checked) => toggleModuleAccess(module.id, checked)}
                          data-testid={`module-checkbox-${module.id}`}
                        />
                        <label 
                          htmlFor={`module-${module.id}`}
                          className="font-medium cursor-pointer flex items-center gap-2"
                        >
                          <span>{module.icon}</span>
                          {module.label}
                        </label>
                      </div>
                      
                      {hasAccess && (
                        <div className="flex items-center gap-3">
                          {ACTIONS.map(action => (
                            <label key={action} className="flex items-center gap-1.5 text-sm cursor-pointer">
                              <Checkbox
                                checked={modulePerms.includes(action)}
                                onCheckedChange={() => toggleAction(module.id, action)}
                                disabled={action === 'view'} // View is always required
                                data-testid={`action-${module.id}-${action}`}
                              />
                              <span className="capitalize">{action}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermDialog(false)}>
              Cancel
            </Button>
            <Button onClick={savePermissions} disabled={savingPerms}>
              {savingPerms ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Permissions
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffPage;
